import React, {
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Paper, Modal, Typography, Fab, useMediaQuery } from "@mui/material";
import styled from "styled-components";
import theme from "theme";
import CloseIcon from "@mui/icons-material/Close";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import { useToCDNUrl } from "hooks/useCDN";
import { useDraggable } from "@dnd-kit/core";
import { clamp } from "modules/clamp";

const GRAB_SIZE = 50;

const Inspector: React.FC = () => {
  const d = useAppDispatch();
  const toCDNUrl = useToCDNUrl();
  const imageRef = useRef<HTMLImageElement>(null);
  const centeringRef = useRef<HTMLDivElement>(null);
  const [openInspector, imageUrl, text] = useAppSelector(
    (state) =>
      [
        store.getAppState(state, "openInspector"),
        store.getAppState(state, "inspectImageUrl"),
        store.getAppState(state, "inspectText"),
      ] as const,
    shallowEqual
  );
  const onClose = useCallback(() => {
    d(
      store.appStateMutate((state) => {
        state.openInspector = false;
      })
    );
  }, [d]);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: "inspector",
    disabled: isMobile,
  });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const position = useRef({
    x: 0,
    y: 0,
  });
  const prevPosition = useRef({
    x: 0,
    y: 0,
  });
  const [isOverHeight, setIsOverHeight] = useState<boolean | null>(null);

  const getWindowPositionX = useCallback(() => {
    const contentPosition = prevPosition.current.x + (transform?.x ?? 0);
    const rectWidth = imageRef.current?.getBoundingClientRect().width ?? 0;
    const limitWidth = windowSize.width / 2 + rectWidth / 2 - GRAB_SIZE;
    // Mobileサイズの場合は移動ができないため中央に移動
    return isMobile
      ? 0
      : clamp(contentPosition, {
          min: -limitWidth,
          max: limitWidth,
        });
  }, [transform, windowSize, isMobile]);

  const getWindowPositionY = useCallback(() => {
    const contentPosition = prevPosition.current.y + (transform?.y ?? 0);
    const rectHeight =
      imageRef.current?.getBoundingClientRect().height ?? GRAB_SIZE;
    const centering = centeringRef.current?.getBoundingClientRect().top ?? 0;
    // Mobileサイズの場合は移動ができないため中央に移動
    return isMobile
      ? 0
      : clamp(contentPosition, {
          min: -(rectHeight + centering - GRAB_SIZE),
          max: windowSize.height - centering - GRAB_SIZE,
        });
  }, [transform, windowSize, isMobile]);

  const handleWindowResize = useCallback(() => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    if (!openInspector) {
      setIsOverHeight(null);
      return;
    }

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [openInspector, handleWindowResize]);

  const style = {
    transform: `translate3d(${getWindowPositionX()}px, ${getWindowPositionY()}px, 0)`,
  };

  useEffect(() => {
    if (transform) {
      position.current.x = getWindowPositionX();
      position.current.y = getWindowPositionY();
    } else {
      prevPosition.current.x = position.current.x;
      prevPosition.current.y = position.current.y;
    }
  }, [transform, getWindowPositionX, getWindowPositionY]);

  useEffect(() => {
    if (!openInspector) {
      position.current = {
        x: 0,
        y: 0,
      };
      prevPosition.current = {
        x: 0,
        y: 0,
      };
    }
  }, [openInspector]);

  const handleImageLoad = () => {
    const imageHeight = imageRef.current?.clientHeight;
    const windowHeight = window.innerHeight;

    if (imageHeight && imageHeight > windowHeight) {
      setIsOverHeight(true);
    } else {
      setIsOverHeight(false);
    }
  };

  const handleOnClick = useCallback(
    (e: MouseEvent) => {
      // 拡大画像をドラッグしてもCenteringの位置は変わらないので、Centeringの領域がクリックされたら閉じる
      if (
        centeringRef.current &&
        centeringRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Modal
      open={openInspector && !!imageUrl}
      onClose={onClose}
      onClick={handleOnClick}
      disableRestoreFocus
    >
      <Centering ref={centeringRef} isover={isOverHeight}>
        <Container
          ref={setNodeRef}
          {...attributes}
          {...listeners}
          style={style}
        >
          <CloseFab size="small" onClick={onClose} data-no-dnd="true">
            <CloseIcon />
          </CloseFab>
          <Inner>
            {imageUrl ? (
              <Figure>
                <img
                  ref={imageRef}
                  src={toCDNUrl(imageUrl)}
                  width="100%"
                  draggable={false}
                  onLoad={handleImageLoad}
                />
              </Figure>
            ) : null}
            {text ? (
              <Paragraph>
                <Typography style={{ whiteSpace: "pre-wrap" }}>
                  {text}
                </Typography>
              </Paragraph>
            ) : null}
          </Inner>
        </Container>
      </Centering>
    </Modal>
  );
};

const Centering = styled.div<{ isover: boolean | null }>`
  max-width: 960px;
  max-height: 960px;
  position: absolute;
  top: ${(props) => (props.isover ? "10%" : "50%")};
  left: 50%;
  ${(props) =>
    props.isover
      ? "transform: translateX(-50%)"
      : "transform: translate(-50%, -50%)"};
  outline: none;
  opacity: ${(props) => (props.isover === null ? 0 : 1)};
  ${theme.breakpoints.down("sm")} {
    left: 16px;
    right: 16px;
    ${(props) =>
      props.isover ? "transform: none;" : "transform: translateY(-50%)"};
  }
`;

const Container = styled(Paper)`
  background: #222;
`;

const Figure = styled.figure`
  background: #111;
`;

const CloseFab = styled(Fab)`
  position: absolute;
  top: -16px;
  right: -16px;
`;

const Inner = styled.div`
  box-sizing: border-box;
  padding: 8px;
  flex: 1;
  overflow: auto;
`;

const Paragraph = styled.div`
  padding: 8px;
`;

export default memo(Inspector);
